// components/library/ProjectTypeSelect.jsx
import React, { Component } from "react";
import { urlMap, getAuthHeaders } from "../../../utils";
import ActionButton from "../../components/ActionButton";

export default class ProjectTypeSelect extends Component {
  state = {
    open: false,
    loading: false,
    error: null,
    types: [],           // [{ id, key, name, description, structure }]
    assigningId: null,   // id while PUT is in flight
    showSaved: false,    // ephemeral success badge
  };

  _root = null;
  _savedTimer = null;

  _outside = (e) => {
    if (!this._root) return;
    if (!this.state.open) return;
    if (!this._root.contains(e.target)) {
      this.setState({ open: false });
    }
  };

  componentDidMount() {
    this.loadTypes();
    document.addEventListener("mousedown", this._outside);
    document.addEventListener("keydown", this.handleKeydown);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this._outside);
    document.removeEventListener("keydown", this.handleKeydown);
    if (this._savedTimer) clearTimeout(this._savedTimer);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.project?.project_type_key !== this.props.project?.project_type_key) {
      this.setState({ open: false, assigningId: null });
    }
  }

  handleKeydown = (e) => {
    if (e.key === "Escape" && this.state.open) {
      this.setState({ open: false });
    }
  };

  pickTypesUrl = () => {
    if (urlMap?.listProjectTypes) return urlMap.listProjectTypes;
    if (urlMap?.projectTypeEndpoint) return urlMap.projectTypeEndpoint;
    return "/core/project-types";
  };

  loadTypes = async () => {
    this.setState({ loading: true, error: null });
    try {
      const headers = { Accept: "application/json", ...getAuthHeaders() };
      const res = await fetch(this.pickTypesUrl(), { method: "GET", headers, credentials: "include" });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`HTTP_${res.status}${text ? ` · ${text.slice(0, 120)}` : ""}`);
      }
      const ct = (res.headers.get("content-type") || "").toLowerCase();
      const types = ct.includes("application/json") ? await res.json() : [];
      this.setState({ types, loading: false });
    } catch (e) {
      console.error("ProjectTypeSelect.loadTypes:", e);
      this.setState({ loading: false, error: String(e?.message || e) });
    }
  };

  assignType = async (typeIdOrNull) => {
    if (this.isReadOnly()) {
      alert("This project is archived. Restore it first to change the type.");
      return;
    }
    if (!this.props.project?.id) return;

    this.setState({ assigningId: typeIdOrNull || "__clear__" });
    try {
      const selected = typeIdOrNull
        ? this.state.types.find((t) => t.id === typeIdOrNull) || null
        : null;

      const updatedProject = await this.props.onApplyPatch?.({
        project_type_key: typeIdOrNull,
      });

      this.props.onApplied?.(updatedProject, selected);

      if (this._savedTimer) clearTimeout(this._savedTimer);
      this.setState({ open: false, assigningId: null, showSaved: true }, () => {
        this._savedTimer = setTimeout(() => {
          this.setState({ showSaved: false });
        }, 1600);
      });
    } catch (e) {
      alert(`Could not update project type: ${e.message}`);
      console.error("ProjectTypeSelect.assignType:", e);
      this.setState({ assigningId: null });
    }
  };

  isSelected = (type) => {
    const currentId = this.props.project?.project_type_key || null;
    return !!(currentId && type?.id && currentId === type.id);
  };

  actsSummary = (type) => {
    const acts = Array.isArray(type?.structure?.acts) ? type.structure.acts : [];
    return acts.length ? `${acts.length} act${acts.length > 1 ? "s" : ""}` : "No act structure";
  };

  triggerLabel = () => {
    const currentId = this.props.project?.project_type_key || null;
    if (!currentId) return "Project type: Not set";
    const current = this.state.types.find((t) => t.id === currentId);
    return `Project type: ${current?.name || "Unknown"}`;
  };

  isReadOnly = () => {
    const { disabled, readOnly, project } = this.props;
    return !!(disabled || readOnly || project?.archived_at);
  };

  renderMenu() {
    const { loading, error, types, assigningId } = this.state;
    const currentId = this.props.project?.project_type_key || null;
    const readOnly = this.isReadOnly();

    if (loading) return <div className="pt-menu-empty">Loading…</div>;
    if (error) return <div className="pt-menu-empty">Could not load project types: {error}</div>;
    if (!types.length) return <div className="pt-menu-empty">No project types yet.</div>;

    return (
      <ul className="pt-options" role="listbox" aria-label="Project types" aria-disabled={readOnly || undefined}>
        <li
          className={`pt-option ${currentId ? "" : "is-selected"}${readOnly ? " is-locked" : ""}`}
          role="option"
          aria-selected={!currentId}
          aria-disabled={readOnly || undefined}
          onClick={() => (!readOnly ? this.assignType(null) : null)}
          title={readOnly ? "Restore this project to change its type" : "Remove type"}
        >
          <div className="pt-option-main">
            <div className="pt-option-title">Not set</div>
            <div className="pt-option-desc">No structure applied</div>
          </div>
          <div className="pt-option-side">
            {assigningId === "__clear__" ? "…" : !currentId ? "✓" : ""}
          </div>
        </li>

        {types.map((t) => {
          const selected = this.isSelected(t);
          const busy = assigningId === t.id;
          return (
            <li
              key={t.id}
              className={`pt-option ${selected ? "is-selected" : ""}${readOnly ? " is-locked" : ""}`}
              role="option"
              aria-selected={selected}
              aria-disabled={readOnly || undefined}
              onClick={() => (!readOnly ? this.assignType(t.id) : null)}
              title={readOnly ? "Restore this project to change its type" : `Select ${t.name}`}
            >
              <div className="pt-option-main">
                <div className="pt-option-title">{t.name}</div>
                {t.description && <div className="pt-option-desc">{t.description}</div>}
                <div className="pt-option-meta">{this.actsSummary(t)}</div>
              </div>
              <div className="pt-option-side">
                {busy ? "…" : selected ? "✓" : ""}
              </div>
            </li>
          );
        })}
      </ul>
    );
  }

  render() {
    const { open, showSaved } = this.state;
    const readOnly = this.isReadOnly();

    return (
      <div className={`pt-select${readOnly ? " is-readonly" : ""}`} ref={(el) => (this._root = el)}>
        <ActionButton
          className="pt-trigger"
          ariaLabel="Change project type"
          ariaPressed={open && !readOnly}
          onClick={() =>
            !readOnly
              ? this.setState((s) => ({ open: !s.open }))
              : alert("This project is archived. Restore it first to change the type.")
          }
          title={
            readOnly
              ? "Archived — use the ⋮ actions to restore before changing type"
              : "Change project type"
          }
        >
          <div className="pt-trigger-left">
            <span className="pt-trigger-label">{this.triggerLabel()}</span>
            {showSaved && !readOnly && (
              <span className="pt-badge success" role="status" aria-live="polite">
                <svg className="icon" width="14" height="14" viewBox="0 0 20 20" aria-hidden="true">
                  <path d="M7.5 13.5l-3-3 1.4-1.4 1.6 1.6 5.6-5.6 1.4 1.4-7 7z" />
                </svg>
                Saved
              </span>
            )}
            {readOnly && (
              <span className="pt-badge muted" role="note" aria-live="polite">
                Archived
              </span>
            )}
          </div>
          <svg
            className={`pt-caret ${open && !readOnly ? "open" : ""}`}
            width="16"
            height="16"
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path d="M5 7l5 6 5-6H5z" />
          </svg>
        </ActionButton>

        {open && !readOnly && (
          <div className="pt-popover" role="dialog">
            {this.renderMenu()}
          </div>
        )}
      </div>
    );
  }
}
